import { ArrowRight, Clock } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative grain pt-32 pb-24 sm:pt-40 sm:pb-32 px-6 sm:px-10 lg:px-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* LEFT - Text */}
        <div className="lg:col-span-7 reveal">
          <p className="eyebrow flex items-center mb-8">
            <span className="gold-line" />
            Manager de transition IT
          </p>

          <h1
            data-testid="hero-name"
            className="font-serif font-light text-[#0B132B] text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
          >
            Olivier
            <br />
            Soutarson
          </h1>

          <p
            data-testid="hero-title"
            className="mt-6 font-serif italic text-2xl sm:text-3xl text-[#16234A] font-light"
          >
            DSI de transition — l'urgentiste IT qu'on appelle
            <br className="hidden sm:block" /> quand le SI est en détresse.
          </p>

          <p className="mt-8 max-w-xl text-[#4A5568] text-base sm:text-lg leading-relaxed">
            Vacance de DSI, crise IT, programme stratégique en difficulté :
            je reprends la main sur la direction opérationnelle et je
            sécurise la continuité de service. Décisions clarifiées,
            gouvernance rétablie, équipes remises en mouvement — dès les
            premières semaines.
          </p>

          {/* Bandeau de preuves — 3 badges premium sobres */}
          <ul
            data-testid="hero-proofs"
            className="mt-10 flex flex-wrap gap-3 max-w-3xl"
          >
            <li className="inline-flex items-baseline gap-2 border border-[#0B132B]/15 px-4 py-2.5 bg-[#FAF8F5]">
              <span className="font-serif italic text-[#C8A97E] text-base leading-none">
                15+
              </span>
              <span className="text-[#0B132B]/85 text-xs uppercase tracking-[0.12em] font-light leading-none">
                ans d'intervention en situations critiques au sein du SI
              </span>
            </li>
            <li className="inline-flex items-baseline gap-2 border border-[#0B132B]/15 px-4 py-2.5 bg-[#FAF8F5]">
              <span className="text-[#0B132B]/85 text-xs uppercase tracking-[0.12em] font-light leading-none">
                Crise, vacance, transformation complexe
              </span>
            </li>
            <li className="inline-flex items-baseline gap-2 border border-[#0B132B]/15 px-4 py-2.5 bg-[#FAF8F5]">
              <span className="text-[#0B132B]/85 text-xs uppercase tracking-[0.12em] font-light leading-none">
                Premier échange confidentiel sous 24&nbsp;h ouvrées
              </span>
            </li>
          </ul>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-3 bg-[#0B132C] text-[#FAF8F5] px-8 py-4 text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#16234A] transition-colors"
            >
              Échanger sur votre situation IT
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="/diagnostic"
              data-testid="hero-cta-diagnostic"
              className="inline-flex items-center gap-3 border border-[#0B132B] text-[#0B132B] px-8 py-4 text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#0B132B] hover:text-[#FAF8F5] transition-colors"
            >
              <Clock size={16} />
              Diagnostic SI — 10 minutes
            </a>
          </div>
        </div>

        {/* RIGHT - Portrait */}
        <div className="lg:col-span-5 reveal">
          <div className="relative mx-auto max-w-md lg:max-w-full">
            {/* gold frame accent */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-[#C8A97E]" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-[#C8A97E]" />

            <div className="relative aspect-[4/5] overflow-hidden bg-[#F0ECE1] shadow-[0_30px_80px_-30px_rgba(11,19,43,0.45)]">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/hero-portrait-640.webp 640w, /hero-portrait-960.webp 960w"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 448px"
                />
                <source
                  type="image/jpeg"
                  srcSet="/hero-portrait-640.jpg 640w, /hero-portrait-960.jpg 960w"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 448px"
                />
                <img
                  data-testid="hero-portrait"
                  src="/hero-portrait-960.jpg"
                  width="960"
                  height="1200"
                  alt="Portrait d'Olivier Soutarson, DSI de transition"
                  className="w-full h-full object-cover object-center transition-transform duration-1000 hover:scale-[1.03]"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                />
              </picture>
              {/* subtle warm overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/15 via-transparent to-transparent pointer-events-none" />
            </div>

            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[#4A5568] text-center lg:text-left">
              <span className="text-[#C8A97E]">—</span> Disponible pour vos missions de transition
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
